import { gql } from 'graphql-tag';
import { useParams } from 'react-router-dom';

import { useInfiniteQuery } from 'src/core/api/hooks/useInfiniteQuery';
import { type InfiniteQueryState } from 'src/core/api/queryState';

import { type EmployeeAccount } from '../../accounting';

const GET_PAGINATED_EMPLOYEE_ACCOUNTS = gql`
  query Id7df11863bad0cb31ac53437082f9b501(
    $companyId: ID!
    $after: ID
    $isMapped: Boolean
    $includeArchived: Boolean
  ) {
    __typename
  }
`;

type RawData = {
  company: {
    chartOfAccounts: {
      employeeAccounts: {
        pageInfo: {
          hasNextPage: boolean;
          cursor?: string;
        };
        edges: {
          cursor: string;
          node: {
            generalAccountCode: string;
            auxiliaryAccountCode: string | undefined;
            isDefault: boolean;
            id: string;
            members: {
              edges: {
                node: {
                  avatar?: string;
                  familyName: string;
                  givenName: string;
                  id: string;
                };
              }[];
            };
          };
        }[];
      };
    };
  };
};

export const usePaginatedEmployeeAccountsQuery = ({
  isMapped,
  includeArchived,
}: {
  isMapped?: boolean;
  includeArchived?: boolean;
}): InfiniteQueryState<EmployeeAccount[]> => {
  const { company: companyId } = useParams();

  return useInfiniteQuery<EmployeeAccount, RawData>({
    key: ['accountsPayable', 'getPaginatedEmployeeAccounts', companyId],
    getRequest: (cursor) => ({
      type: 'graphQL',
      target: 'v2',
      query: GET_PAGINATED_EMPLOYEE_ACCOUNTS,
      variables: {
        after: cursor,
        isMapped,
        includeArchived,
      },
    }),
    getNextPageParam: (data) => {
      const { edges, pageInfo } = data.company.chartOfAccounts.employeeAccounts;
      const lastEdge = edges.at(-1);

      if (pageInfo.hasNextPage && lastEdge) {
        return lastEdge.cursor;
      }
      return undefined;
    },
    reshapeData(paginatedEmployeeAccounts) {
      return paginatedEmployeeAccounts.company.chartOfAccounts.employeeAccounts.edges.flatMap(
        ({ node }) => {
          return node.members.edges.map(({ node: memberNode }) => {
            return {
              id: node.id,
              generalAccountCode: node.generalAccountCode,
              auxiliaryAccountCode: node.auxiliaryAccountCode,
              user: memberNode
                ? {
                    id: memberNode.id,
                    firstName: memberNode.givenName,
                    lastName: memberNode.familyName,
                    avatar: memberNode.avatar || null,
                  }
                : undefined,
            };
          });
        },
      );
    },
  });
};
